/**
 * One confirmation dialog for the Email console, replacing `window.confirm` and `window.alert`.
 *
 * ═══ Why the native dialogs had to go ════════════════════════════════════════
 *
 * They are unstyleable, so they announce that the surrounding product is a prototype. But the
 * functional problems are worse than the cosmetic one:
 *
 *   · `window.confirm` blocks the main thread, so nothing can indicate progress and a slow request
 *     afterwards looks like a hung page.
 *   · Chrome and Safari suppress repeat dialogs from the same page. A user who ticks "prevent this
 *     page from creating more dialogs" silently gets `false` from every later confirm — which, for a
 *     revoke or a close-account flow, means the click does nothing and says nothing.
 *   · There is no room to distinguish "this can be undone" from "this cannot". `confirm()` gives one
 *     sentence and two identical buttons, so revoking a key and removing a step look the same.
 *   · `\n\n` in a message is the only formatting available, which is why the strings being replaced
 *     here were written as paragraphs jammed into one argument.
 *
 * ═══ Imperative on purpose ═══════════════════════════════════════════════════
 *
 * A call site changes from
 *     if (!window.confirm("Revoke?")) return;
 * to
 *     if (!(await confirm({ title: "Revoke?", variant: "destructive" }))) return;
 *
 * — the same shape, so the control flow at 13 call sites did not have to be restructured into state
 * and callbacks. This mirrors `askConfirm` in the AI product's `ui/confirm-dialog.tsx` deliberately:
 * two products, two design languages, one mental model for the developer moving between them.
 *
 * ═══ What this does NOT do ═══════════════════════════════════════════════════
 *
 * It is not a toast and not a notification centre. `notify()` exists only because `window.alert` was
 * being used to surface request failures, and those call sites needed an equivalent that is not a
 * native dialog. Anything richer belongs to whatever notification system comes later.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Icon, type IconName } from "./Icon";

/**
 * `destructive` removes or stops something. `warning` changes a setting whose effect is significant
 * but reversible. `info` asks a question that is neither.
 *
 * Three, not more: a vocabulary a caller has to think about is one they will pick from at random.
 */
export type ConfirmVariant = "destructive" | "warning" | "info";

export interface ConfirmOptions {
  title: string;
  /** Paragraphs. An array so the copy can breathe — `confirm()` could only take one string. */
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  /**
   * When set, the exact string the user must type before the primary button enables.
   *
   * For actions where a mis-click is unrecoverable — closing an account, revoking a live key. The
   * comparison is trimmed but case-SENSITIVE: "delete" is easy to type absent-mindedly, "DELETE" is
   * a decision.
   */
  requireText?: string;
  /** Label above the typed-confirmation field, when the default reads awkwardly. */
  requireTextLabel?: ReactNode;
}

/** A notice with a single dismissing button — the `window.alert` replacement. */
export interface NoticeOptions {
  title: string;
  body?: ReactNode;
  variant?: ConfirmVariant;
  dismissLabel?: string;
}

interface Api {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  notify: (opts: NoticeOptions) => Promise<void>;
}

type Pending =
  | { kind: "confirm"; opts: ConfirmOptions; resolve: (ok: boolean) => void }
  | { kind: "notice"; opts: NoticeOptions; resolve: () => void };

const Ctx = createContext<Api | null>(null);

/*
 * `critical` rather than a bin: the octagon is about severity, which is what a confirmation is asking
 * about. A bin would also be wrong for half these call sites — revoking a key and stopping a sequence
 * destroy no object.
 *
 * The three shapes are octagon / triangle / circle, so the variants are distinguishable without
 * relying on the red-amber-blue difference. Icon.tsx makes the same point about its severity set.
 */
const ICON: Record<ConfirmVariant, IconName> = {
  destructive: "critical",
  warning: "warning",
  info: "info",
};

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [typed, setTyped] = useState("");

  /*
   * Guards against resolving twice. Esc, the backdrop, the Cancel button and Enter can all settle the
   * same dialog, and a promise that resolves twice would silently drop the second answer — which for
   * `confirm` means an action running when the user pressed Escape.
   */
  const settled = useRef(false);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  /** Focus is returned here on close, so keyboard users are not dropped at the top of the page. */
  const returnFocusTo = useRef<HTMLElement | null>(null);

  const open = pending !== null;

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        returnFocusTo.current = document.activeElement as HTMLElement | null;
        settled.current = false;
        setTyped("");
        setPending({ kind: "confirm", opts, resolve });
      }),
    [],
  );

  const notify = useCallback(
    (opts: NoticeOptions) =>
      new Promise<void>((resolve) => {
        returnFocusTo.current = document.activeElement as HTMLElement | null;
        settled.current = false;
        setTyped("");
        setPending({ kind: "notice", opts, resolve });
      }),
    [],
  );

  const api = useMemo<Api>(() => ({ confirm, notify }), [confirm, notify]);

  const needsText = pending?.kind === "confirm" && Boolean(pending.opts.requireText);
  const canConfirm =
    pending?.kind !== "confirm" ||
    !pending.opts.requireText ||
    typed.trim() === pending.opts.requireText;

  const settle = useCallback(
    (ok: boolean) => {
      if (!pending || settled.current) return;
      settled.current = true;
      if (pending.kind === "confirm") pending.resolve(ok);
      else pending.resolve();
      setPending(null);
      setTyped("");
      // After the dialog unmounts, or the element is still hidden when focus is requested.
      requestAnimationFrame(() => returnFocusTo.current?.focus?.());
    },
    [pending],
  );

  /* ── Keyboard ──────────────────────────────────────────────────────────────
   *
   * Captured at the document, so a keystroke inside the dialog's input still reaches this, and
   * `stopPropagation` somewhere in the tree cannot leave Escape dead.
   *
   * Escape always cancels — for a notice that is the same as dismissing, which is what a user
   * pressing Escape means either way.
   *
   * Enter confirms ONLY when the action is not gated by typed text. Where it is gated, the whole
   * point is a deliberate act, and letting Enter through from anywhere on the page would undo that.
   */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        settle(false);
        return;
      }
      if (e.key === "Enter") {
        // Never hijack Enter inside a textarea, where it means "new line".
        if ((e.target as HTMLElement | null)?.tagName === "TEXTAREA") return;
        if (!canConfirm) return;
        e.preventDefault();
        settle(true);
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, canConfirm, settle]);

  /* Body scroll lock: a dialog that scrolls the page behind it does not feel modal. */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  /*
   * Initial focus: the typed-confirmation field when there is one, otherwise the confirm button.
   *
   * Not the confirm button when text is required — landing on a disabled control tells a screen
   * reader user nothing about what to do next, whereas the field is the thing to act on.
   */
  useEffect(() => {
    if (!open) return;
    const t = requestAnimationFrame(() => {
      if (needsText) inputRef.current?.focus();
      else confirmRef.current?.focus();
    });
    return () => cancelAnimationFrame(t);
  }, [open, needsText]);

  /*
   * Keep Tab inside the dialog. Not a full focus-trap library: the dialog contains at most three
   * focusable controls, so cycling the first and last is the whole requirement.
   */
  useEffect(() => {
    if (!open) return;
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !dialogRef.current) return;
      const items = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onTab, true);
    return () => document.removeEventListener("keydown", onTab, true);
  }, [open]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {open && pending && createPortal(
        <div
          className="pa-modal-backdrop"
          // A click on the backdrop cancels, matching Escape. Notices dismiss the same way.
          onMouseDown={(e) => { if (e.target === e.currentTarget) settle(false); }}
        >
          <div
            ref={dialogRef}
            className="pa-modal"
            role={pending.kind === "confirm" ? "alertdialog" : "dialog"}
            aria-modal="true"
            aria-labelledby="pa-modal-title"
            aria-describedby={pending.opts.body ? "pa-modal-body" : undefined}
          >
            <div className="flex items-start gap-3">
              <span
                className="pa-modal-chip"
                data-variant={pending.opts.variant ?? "info"}
                aria-hidden="true"
              >
                <Icon name={ICON[pending.opts.variant ?? "info"]} size={16} />
              </span>
              <div className="min-w-0 flex-1">
                <h2
                  id="pa-modal-title"
                  style={{ fontSize: "var(--text-section)", fontWeight: 600, color: "var(--text)" }}
                >
                  {pending.opts.title}
                </h2>
                {pending.opts.body && (
                  <div
                    id="pa-modal-body"
                    className="mt-1.5 flex flex-col gap-2"
                    style={{ fontSize: "var(--text-body)", color: "var(--text-2)", lineHeight: 1.5 }}
                  >
                    {pending.opts.body}
                  </div>
                )}
              </div>
            </div>

            {pending.kind === "confirm" && pending.opts.requireText && (
              <label className="mt-4 block">
                <span className="micro mb-1 block" style={{ color: "var(--text-2)" }}>
                  {pending.opts.requireTextLabel ?? (
                    <>
                      Type <strong style={{ color: "var(--text)" }}>{pending.opts.requireText}</strong> to confirm
                    </>
                  )}
                </span>
                <input
                  ref={inputRef}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={pending.opts.requireText}
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="h-[34px] w-full px-[10px]"
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius)",
                    background: "var(--canvas)",
                    fontSize: "var(--text-dense)",
                    color: "var(--text)",
                  }}
                />
              </label>
            )}

            <div className="mt-5 flex justify-end gap-2">
              {pending.kind === "confirm" && (
                <button type="button" className="pa-btn" onClick={() => settle(false)}>
                  {pending.opts.cancelLabel ?? "Cancel"}
                </button>
              )}
              <button
                ref={confirmRef}
                type="button"
                className={
                  pending.kind === "notice"
                    ? "pa-btn pa-btn-primary"
                    : (pending.opts.variant ?? "info") === "destructive"
                      ? "pa-btn pa-btn-danger"
                      : "pa-btn pa-btn-primary"
                }
                disabled={!canConfirm}
                onClick={() => settle(true)}
              >
                {pending.kind === "notice"
                  ? (pending.opts.dismissLabel ?? "OK")
                  : (pending.opts.confirmLabel ??
                     ((pending.opts.variant ?? "info") === "destructive" ? "Delete" : "Confirm"))}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  );
}

/**
 * The dialogs, for a call site.
 *
 * THROWS when the provider is missing, rather than falling back to `window.confirm`.
 *
 * The AI product's equivalent does fall back, reasoning that a call site must never proceed without
 * asking. That is sound, but it also means an unmounted provider ships a native dialog to production
 * and nobody finds out — which is the exact state this change exists to end. A thrown error surfaces
 * in development, at the first click, on the developer who removed the provider.
 */
export function useConfirm(): Api {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      "useConfirm() requires <ConfirmProvider>. It is mounted at the root of App.tsx — if you are " +
      "rendering a component in isolation, wrap it in <ConfirmProvider>.",
    );
  }
  return ctx;
}
